import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type EmailVerifyResult, workflowsApi } from '@/api/workflows';

import { cacheVerdict, readCachedVerdict } from './emailVerifyCache';
import { type EmailVerifyLookup, isVerifiable, normalizeEmail } from './emailVerifyGate';

/**
 * The ZeroBounce store behind every ordinary email input — one instance per
 * {@link ./FormShell} mount, handed to the field tree through
 * {@link ./emailVerifyContext} and read by the Next gate in the same render.
 *
 * <h3>One attempt per address, for the life of the mount</h3>
 * `request` is idempotent per normalized address, and that is load-bearing
 * rather than merely thrifty. The renderers re-run their whole blur chain when
 * a verdict lands — that is how a message that arrived after the person already
 * tabbed away gets on screen — and that chain calls `request` again. Without
 * the attempted-set the sequence would be: request → in-flight → re-render →
 * blur chain → request → … a render loop that bills ZeroBounce once per turn.
 *
 * So an address is asked about exactly once. Note this includes an `Unknown`:
 * unlike {@link ./OneSpanESignStep}, which deliberately refuses to cache a
 * failed lookup so re-entering the step retries it, there is nothing to retry
 * FOR here — `Unknown` passes this gate (see {@link ./emailVerifyGate}), so a
 * failed lookup costs the person filling the form exactly nothing. Editing the
 * address produces a different key and is attempted fresh, which is the only
 * user action that could change the answer anyway.
 *
 * <h3>And one lookup per address, across mounts</h3>
 * The attempted-set above only reaches as far as the mount that owns it, so a
 * remount — leaving an application and coming back, any route change that drops
 * the shell — used to re-ask about every address the advisor had already filled
 * in. {@link ./emailVerifyCache} is the mount-independent half: `request`
 * consults it first and serves a stored verdict without a round-trip, and the
 * flush writes every verdict it receives back. The attempted-set stays, because
 * the two answer different questions — it covers addresses that are in flight
 * and therefore NOT yet in the cache, which is the state the render loop above
 * spins in.
 *
 * <h3>Coalescing</h3>
 * Blur is user-paced, so requests rarely overlap on their own — but tabbing
 * briskly through a repeatable table of email cells, or a paste-and-tab, will
 * stack several within a few hundred milliseconds. Collecting those into one
 * POST keeps a table of ten addresses at one call against the `EmailVerify`
 * rate-limit window instead of ten. The endpoint has always been batch-shaped;
 * this just uses it.
 */
export type EmailVerification = {
  /**
   * Ask about one raw field value. Silently does nothing when the value is
   * blank, fails the local syntax rule, or has been asked about already.
   */
  request: (raw: string) => void;
  /** Read one normalized address's state. Stable key via {@link normalizeEmail}. */
  lookup: EmailVerifyLookup;
};

/** How long requests are collected before one batched POST goes out. */
const COALESCE_MS = 300;

/**
 * @param enabled False switches the whole thing off and returns null — what the
 *   public customer runtime gets. Null rather than a no-op object so a consumer
 *   cannot accidentally treat "capability off" as "no verdict yet": the two mean
 *   the same for the gate, but only one of them should ever render a spinner.
 */
export function useEmailVerification(enabled: boolean): EmailVerification | null {
  const [results, setResults] = useState<Record<string, EmailVerifyResult>>({});
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(() => new Set());

  // Every address ever asked about this mount, settled or not. See the
  // loop note in the type doc above — this is the thing that terminates it.
  const attemptedRef = useRef<Set<string>>(new Set());
  // Queued for the next flush. Separate from `inFlight` (which is state, and
  // includes addresses already on the wire) so the flush knows what to send.
  const pendingRef = useRef<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set on unmount so a response landing afterwards doesn't setState on a dead
  // component. The renderers can unmount mid-request on any step navigation.
  const deadRef = useRef(false);

  useEffect(() => {
    deadRef.current = false;
    return () => {
      deadRef.current = true;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, []);

  const flush = useCallback(() => {
    timerRef.current = null;
    const batch = [...pendingRef.current];
    pendingRef.current.clear();
    if (batch.length === 0) return;

    void workflowsApi
      .verifyEmails(batch)
      .then((received) => {
        // Written to the cross-mount cache BEFORE the dead check: the verdict is just as
        // good to the next mount whether or not this one is still around to render it, and
        // a step navigation landing between the POST and its response is ordinary.
        // `cacheVerdict` drops `Unknown` itself — see that module for why.
        for (const r of received) cacheVerdict(r);
        if (deadRef.current) return;
        setResults((prev) => {
          const next = { ...prev };
          for (const r of received) next[normalizeEmail(r.email)] = r;
          return next;
        });
      })
      .finally(() => {
        if (deadRef.current) return;
        // Clear the whole batch, not just what came back: an address the
        // response omitted is settled too — settled as "we have no verdict" —
        // and leaving it marked in-flight would hold Next forever.
        setInFlight((prev) => {
          const next = new Set(prev);
          for (const e of batch) next.delete(e);
          return next;
        });
      });
  }, []);

  const request = useCallback(
    (raw: string) => {
      if (!isVerifiable(raw)) return;
      const email = normalizeEmail(raw);
      if (attemptedRef.current.has(email)) return;
      attemptedRef.current.add(email);

      // A verdict an earlier mount already paid for. Seed it and stop: queueing would buy
      // an answer we hold, and marking it in-flight would hold Next for a request that is
      // never going to be made. Only a MISS continues to the wire.
      const cached = readCachedVerdict(email);
      if (cached) {
        setResults((prev) => ({ ...prev, [email]: cached }));
        return;
      }

      pendingRef.current.add(email);
      // Marked in-flight the moment it is QUEUED, not when it goes on the wire.
      // The coalescing window is dead time during which Next must already be
      // held — otherwise the window is exactly the gap a fast clicker walks
      // through, which is the hole `checking` exists to close.
      setInFlight((prev) => {
        const next = new Set(prev);
        next.add(email);
        return next;
      });
      if (timerRef.current === null) timerRef.current = setTimeout(flush, COALESCE_MS);
    },
    [flush],
  );

  const lookup = useCallback<EmailVerifyLookup>(
    (email) => ({ result: results[email], inFlight: inFlight.has(email) }),
    [results, inFlight],
  );

  return useMemo(
    () => (enabled ? { request, lookup } : null),
    // `request` is stable; `lookup` changes identity whenever a verdict lands,
    // which is precisely when the field tree and the Next gate need to re-read.
    [enabled, request, lookup],
  );
}
